import { supabase } from '../lib/supabase';

/**
 * Teacher Analytics Service for fetching comprehensive dashboard metrics
 */

/**
 * Get comprehensive analytics for a teacher
 * @param {string} teacherId - The teacher's ID
 * @returns {Promise<{analytics: Object, error: null} | {analytics: null, error: string}>}
 */
export const getTeacherAnalytics = async (teacherId) => {
  try {
    // Fetch all learning trails for this teacher
    const { data: trails, error: trailsError } = await supabase?.from('learning_trails')?.select('id, title, classroom_id, trail_classrooms(classrooms(classroom_name))')?.eq('teacher_id', teacherId);

    if (trailsError) {
      console.error('Error fetching trails:', trailsError);
      return { analytics: null, error: trailsError?.message };
    }

    const totalJourneys = trails?.length || 0;
    const trailIds = trails?.map(t => t?.id) || [];

    // Fetch all students for this teacher
    const { data: students, error: studentsError } = await supabase?.from('students')?.select('id, full_name, classroom_id, status, last_login_at')?.eq('teacher_id', teacherId)?.eq('status', 'active');

    if (studentsError) {
      console.error('Error fetching students:', studentsError);
      return { analytics: null, error: studentsError?.message };
    }

    // Fetch all student progress for these trails
    const { data: progressData, error: progressError } = await supabase?.from('student_progress')?.select('*')?.in('trail_id', trailIds?.length > 0 ? trailIds : ['00000000-0000-0000-0000-000000000000']);

    if (progressError) {
      console.error('Error fetching progress:', progressError);
      return { analytics: null, error: progressError?.message };
    }

    // Calculate active students (all students with active status)
    const activeStudents = students?.length || 0;

    // Calculate average completion rate
    const completionRates = progressData?.map(p => p?.completion_percentage || 0) || [];
    const avgCompletionRate = completionRates?.length > 0
      ? Math.round(completionRates?.reduce((sum, rate) => sum + rate, 0) / completionRates?.length)
      : 0;

    // Calculate pending questions (quiz attempts that need review)
    const pendingQuestions = progressData?.filter(p => {
      const answersData = p?.answers_data || {};
      return answersData?.quiz_scores && answersData?.quiz_scores?.length > 0;
    })?.length || 0;

    // Get recent activity (last 10 activities)
    const recentActivity = progressData
      ?.sort((a, b) => new Date(b.last_accessed_at) - new Date(a.last_accessed_at))
      ?.slice(0, 10)
      ?.map(progress => {
        const student = students?.find(s => s?.id === progress?.student_id);
        const trail = trails?.find(t => t?.id === progress?.trail_id);
        
        let activityType = 'started';
        let icon = 'Play';
        let color = 'text-primary';
        let bgColor = 'bg-primary/10';
        
        if (progress?.completed_at) {
          activityType = 'completed';
          icon = 'CheckCircle';
          color = 'text-success';
          bgColor = 'bg-success/10';
        } else if (progress?.completion_percentage > 0) {
          activityType = 'in progress';
          icon = 'Activity';
          color = 'text-accent';
          bgColor = 'bg-accent/10';
        }

        const timeAgo = getTimeAgo(new Date(progress.last_accessed_at));

        return {
          id: progress?.id,
          message: `${student?.full_name || 'Unknown Student'} ${activityType} "${trail?.title || 'Unknown Trail'}"`,
          time: timeAgo,
          icon,
          color,
          bgColor
        };
      }) || [];

    // Get top performing journeys (by completion rate)
    const trailPerformance = trails?.map(trail => {
      const trailProgress = progressData?.filter(p => p?.trail_id === trail?.id) || [];
      const completedCount = trailProgress?.filter(p => p?.completed_at)?.length;
      const totalAttempts = trailProgress?.length;
      const completionRate = totalAttempts > 0
        ? Math.round((completedCount / totalAttempts) * 100)
        : 0;

      const classroomName = trail?.trail_classrooms?.[0]?.classrooms?.classroom_name || 'No classroom';

      return {
        id: trail?.id,
        title: trail?.title,
        classroom: classroomName,
        completionRate,
        students: totalAttempts
      };
    }) || [];

    const topJourneys = trailPerformance?.sort((a, b) => b?.completionRate - a?.completionRate)?.slice(0, 5);

    const analytics = {
      totalJourneys,
      activeStudents,
      avgCompletionRate,
      pendingQuestions,
      recentActivity,
      topJourneys
    };

    return { analytics, error: null };
  } catch (error) {
    console.error('Get teacher analytics exception:', error);
    return { analytics: null, error: 'Failed to fetch analytics' };
  }
};

/**
 * Get detailed trail analytics
 * @param {string} trailId - The trail's ID
 * @returns {Promise<{analytics: Object, error: null} | {analytics: null, error: string}>}
 */
export const getTrailAnalytics = async (trailId) => {
  try {
    const { data: progressData, error } = await supabase?.from('student_progress')?.select(`
        *,
        students(full_name, classroom_id)
      `)?.eq('trail_id', trailId);

    if (error) {
      console.error('Error fetching trail analytics:', error);
      return { analytics: null, error: error?.message };
    }

    const totalAttempts = progressData?.length || 0;
    const completedCount = progressData?.filter(p => p?.completed_at)?.length || 0;
    const inProgressCount = progressData?.filter(p => !p?.completed_at && p?.completion_percentage > 0)?.length || 0;
    const completionRate = totalAttempts > 0
      ? Math.round((completedCount / totalAttempts) * 100)
      : 0;

    // Calculate average quiz score
    let totalQuizzes = 0;
    let totalCorrect = 0;
    progressData?.forEach(progress => {
      const answersData = progress?.answers_data || {};
      if (answersData?.quiz_scores) {
        totalQuizzes += answersData?.quiz_scores?.length;
        totalCorrect += answersData?.quiz_scores?.filter(q => q?.correct)?.length;
      }
    });
    const avgQuizScore = totalQuizzes > 0
      ? Math.round((totalCorrect / totalQuizzes) * 100)
      : 0;

    const analytics = {
      totalAttempts,
      completedCount,
      inProgressCount,
      completionRate,
      avgQuizScore,
      totalQuizzes
    };

    return { analytics, error: null };
  } catch (error) {
    console.error('Get trail analytics exception:', error);
    return { analytics: null, error: 'Failed to fetch trail analytics' };
  }
};

/**
 * Helper function to format time ago
 * @param {Date} date - The date to format
 * @returns {string} - Formatted time ago string
 */
function getTimeAgo(date) {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
